import { Accordion, Badge, Box, Button, Flex, Spinner, Switch, Text, Tooltip } from '@rocket/ui';
import { useEffect, useState } from 'react';
import { LuCircleCheck, LuInfo, LuLock, LuSearch } from 'react-icons/lu';
import { toast } from 'react-toastify';
import { useFtueHost } from '../host';
import type { ExtractedRule, ExtractedSection, SlotSurfaceForms } from '../onboarding-api';
import { copyRuleToPlaybook, extractedRuleSampleEdits, saveRules } from '../onboarding-api';
import { CopyMatchesCarousel } from './CopyMatchesCarousel';
import { ManualSearchPanel } from './ManualSearchPanel';
import { SampleEditsCarousel } from './SampleEditsCarousel';

interface MutableRule extends ExtractedRule {
  accepted: boolean;
  isFallback: boolean;
  skipped: boolean;
  copiedFrom?: string;
}

interface MutableSection {
  id: string;
  name: string;
  rules: Array<MutableRule>;
}

interface Props {
  sections: Array<ExtractedSection>;
  playbookConfig: {
    playbookId?: string;
    name: string;
    paperType: string;
    contractType: string;
  } | null;
  slotSurfaceForms?: SlotSurfaceForms;
  isSearchingMatches?: boolean;
  onBack: () => void;
  onComplete: (playbookId?: string, skippedRules?: Array<string>, jobId?: string) => void;
}

export function OnboardingReviewRules({
  sections,
  playbookConfig,
  slotSurfaceForms,
  isSearchingMatches,
  onBack,
  onComplete,
}: Props) {
  const { bbAxios, selectInDocument } = useFtueHost();
  const isFirstParty = playbookConfig?.paperType === 'first_party';
  const [mutableSections, setMutableSections] = useState<Array<MutableSection>>(() =>
    sections.map((s) => ({
      ...s,
      rules: s.rules.map((r) => ({
        ...r,
        skipped: false,
        // Normalize snake_case from the extraction API → camelCase used throughout the UI
        existingRuleId: r.existingRuleId || r.existing_rule_id || undefined,
      })),
    }))
  );
  // When the parent updates `sections` with suggestedMatches from the background
  // match search, merge them into our mutable state without overwriting user edits.
  useEffect(() => {
    console.debug(
      '[ReviewRules] sections prop changed, checking for matches...',
      sections.length,
      'sections'
    );
    const matchLookup = new Map<string, ExtractedRule['suggestedMatches']>();
    for (const s of sections) {
      for (const r of s.rules) {
        if (r.suggestedMatches && r.suggestedMatches.length > 0) {
          matchLookup.set(r.id, r.suggestedMatches);
        }
      }
    }
    console.debug('[ReviewRules] matchLookup has', matchLookup.size, 'rules with matches');
    if (matchLookup.size === 0) {
      return;
    }
    setMutableSections((prev) =>
      prev.map((s) => ({
        ...s,
        rules: s.rules.map((r) => {
          const matches = matchLookup.get(r.id);
          if (matches) {
            console.debug(
              '[ReviewRules] merging',
              matches.length,
              'matches into rule',
              r.id,
              r.name?.slice(0, 40)
            );
          }
          return matches ? { ...r, suggestedMatches: matches } : r;
        }),
      }))
    );
  }, [sections]);

  const [isSaving, setIsSaving] = useState(false);
  const [copyingRuleId, setCopyingRuleId] = useState<string | null>(null);
  const [searchExpanded, setSearchExpanded] = useState<string | null>(null);

  // Scroll to + select the rule's source sentence in a live rendered document.
  // Capability-branch seam: hosts with a live doc (add-in Word; a future in-browser
  // viewer) implement selectInDocument; where it's absent (web upload flow) this is a
  // no-op — the source text is already rendered inline under each rule below.
  const highlightInDocument = async (searchText: string) => {
    if (!selectInDocument) {
      return;
    }
    const query = searchText.trim();
    if (!query) {
      return;
    }
    try {
      await selectInDocument(query);
    } catch {
      toast.error('Unable to navigate to the selected rule text.');
    }
  };

  const totalAccepted = mutableSections.reduce(
    (sum, s) => sum + s.rules.filter((r) => r.accepted && !r.skipped && !r.existingRuleId).length,
    0
  );
  const totalMerged = mutableSections.reduce(
    (sum, s) => sum + s.rules.filter((r) => r.accepted && !!r.existingRuleId).length,
    0
  );
  const totalCopied = mutableSections.reduce(
    (sum, s) => sum + s.rules.filter((r) => r.skipped).length,
    0
  );
  const totalRules = mutableSections.reduce((sum, s) => sum + s.rules.length, 0);

  const setRuleField = (
    sectionId: string,
    ruleId: string,
    field: keyof MutableRule,
    value: boolean | string | null
  ) => {
    setMutableSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              rules: s.rules.map((r) => (r.id === ruleId ? { ...r, [field]: value } : r)),
            }
          : s
      )
    );
  };

  const acceptAll = () => {
    setMutableSections((prev) =>
      prev.map((s) => ({
        ...s,
        rules: s.rules.map((r) => (r.skipped ? r : { ...r, accepted: true })),
      }))
    );
  };

  const copyInsteadOfExtracted = async (
    srcRuleId: string,
    extractedRuleId: string,
    sourcePlaybookName: string,
    sectionName?: string,
    samePlaybook?: boolean
  ) => {
    if (!playbookConfig?.playbookId) {
      return;
    }

    if (samePlaybook) {
      // Same-playbook: mark the rule so the backend adds training to the existing rule
      // instead of creating a duplicate. The rule is NOT skipped — it still gets saved,
      // but the backend will attach the new TUs to the existing rule document.
      setMutableSections((prev) =>
        prev.map((s) => ({
          ...s,
          rules: s.rules.map((r) =>
            r.id === extractedRuleId
              ? { ...r, existingRuleId: srcRuleId, accepted: true, skipped: false }
              : r
          ),
        }))
      );
      setSearchExpanded(null);
      toast.success(`Will add training to existing rule in "${sourcePlaybookName}"`);
      return;
    }

    setCopyingRuleId(extractedRuleId);
    try {
      await copyRuleToPlaybook(bbAxios, {
        sourceRuleId: srcRuleId,
        playbookId: playbookConfig.playbookId,
        sectionName,
      });
      setMutableSections((prev) =>
        prev.map((s) => ({
          ...s,
          rules: s.rules.map((r) =>
            r.id === extractedRuleId
              ? { ...r, skipped: true, copiedFrom: sourcePlaybookName, accepted: false }
              : r
          ),
        }))
      );
      setSearchExpanded(null);
      toast.success(`Copied rule from ${sourcePlaybookName}`);
    } catch {
      toast.error('Failed to copy rule');
    } finally {
      setCopyingRuleId(null);
    }
  };

  const handleSaveRules = async () => {
    setIsSaving(true);
    try {
      // Filter out skipped/copied rules AND rejected rules (accepted: false)
      const sectionsToSave: Array<ExtractedSection> = mutableSections.map((s) => ({
        ...s,
        rules: s.rules.filter((r) => !r.skipped && r.accepted),
      }));
      const result = await saveRules(bbAxios, {
        playbookId: playbookConfig?.playbookId ?? '',
        playbookName: playbookConfig?.name ?? 'My Playbook',
        contractType: playbookConfig?.contractType ?? 'nda',
        paperType: playbookConfig?.paperType === 'first_party' ? 'first_party' : 'third_party',
        sections: sectionsToSave,
        slotSurfaceForms,
      });
      const parts = [`${totalAccepted} new`];
      if (totalMerged > 0) {
        parts.push(`${totalMerged} merged into existing`);
      }
      if (totalCopied > 0) {
        parts.push(`${totalCopied} copied`);
      }
      toast.success(`Rules saved: ${parts.join(', ')}`);
      onComplete(result.playbookId || playbookConfig?.playbookId, undefined, result.jobId);
    } catch {
      toast.error('Failed to save rules. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const accordionItems = mutableSections.map((section) => {
    const acceptedCount = section.rules.filter((r) => r.accepted && !r.skipped).length;
    const copiedCount = section.rules.filter((r) => r.skipped).length;
    const matchCount = section.rules.filter(
      (r) => !r.skipped && r.suggestedMatches && r.suggestedMatches.length > 0
    ).length;
    const headerParts = [
      `${section.name} (${acceptedCount + copiedCount}/${section.rules.length})`,
    ];
    if (matchCount > 0) {
      headerParts.push(`\u2022 ${matchCount} similar found`);
    }
    return {
      value: section.id,
      title: headerParts.join(' '),
      content: (
        <Flex.V gap="3">
          {section.rules.map((rule) => (
            <Box
              key={rule.id}
              borderWidth="1px"
              borderRadius="md"
              p="3"
              bg={
                rule.skipped
                  ? 'blue.subtle'
                  : rule.existingRuleId
                    ? 'orange.subtle'
                    : rule.accepted
                      ? 'green.subtle'
                      : 'bg.surface'
              }
              borderColor={
                rule.skipped
                  ? 'blue.muted'
                  : rule.existingRuleId
                    ? 'orange.muted'
                    : rule.accepted
                      ? 'green.muted'
                      : 'border'
              }
            >
              <Flex.V gap="2">
                <Flex.H align="center" gap="2" wrap="wrap">
                  {selectInDocument ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      px="0"
                      minH="auto"
                      h="auto"
                      onClick={() => {
                        const sample = extractedRuleSampleEdits(rule)[0];
                        const query =
                          rule.sourceText ||
                          rule.source_text ||
                          rule.originalText ||
                          rule.original_text ||
                          sample?.before ||
                          '';
                        void highlightInDocument(query);
                      }}
                    >
                      <Text fontWeight="600" fontSize="sm" textDecoration="underline">
                        {rule.name}
                      </Text>
                    </Button>
                  ) : (
                    <Text fontWeight="600" fontSize="sm">
                      {rule.name}
                    </Text>
                  )}
                  {isFirstParty && (
                    <Badge size="xs" colorPalette="orange" variant="subtle">
                      <LuLock /> Protected
                    </Badge>
                  )}
                  {isFirstParty && rule.isFallback && (
                    <Badge size="xs" colorPalette="blue" variant="subtle">
                      Fallback
                    </Badge>
                  )}
                </Flex.H>
                <Text fontSize="sm" color="fg.muted">
                  {rule.description}
                </Text>

                {rule.skipped ? (
                  <Flex.H align="center" gap="2">
                    <LuCircleCheck size={14} color="var(--chakra-colors-green-fg)" />
                    <Text fontSize="sm">
                      Using rule from <strong>{rule.copiedFrom}</strong>
                    </Text>
                  </Flex.H>
                ) : rule.existingRuleId ? (
                  <Flex.H align="center" gap="2" justify="space-between">
                    <Flex.H align="center" gap="1.5">
                      <LuCircleCheck size={14} color="var(--chakra-colors-orange-fg)" />
                      <Text fontSize="sm">Will add training to existing rule in this playbook</Text>
                    </Flex.H>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => setRuleField(section.id, rule.id, 'existingRuleId', null)}
                    >
                      Create as new instead
                    </Button>
                  </Flex.H>
                ) : (
                  <>
                    {rule.sourceText && (
                      <Text fontSize="xs" fontStyle="italic" color="fg.subtle">
                        &ldquo;{rule.sourceText}&rdquo;
                      </Text>
                    )}
                    {(() => {
                      const edits = extractedRuleSampleEdits(rule);
                      return edits.length > 0 ? (
                        <SampleEditsCarousel
                          edits={edits}
                          onEditChange={(updated) => {
                            const cat = rule.edit_category ?? rule.editCategory ?? 'revise';
                            if (cat === 'insert') {
                              setRuleField(section.id, rule.id, 'edited_text', updated.after);
                            } else if (cat === 'delete') {
                              setRuleField(section.id, rule.id, 'original_text', updated.before);
                            } else {
                              setRuleField(section.id, rule.id, 'original_text', updated.before);
                              setRuleField(section.id, rule.id, 'edited_text', updated.after);
                            }
                          }}
                        />
                      ) : null;
                    })()}

                    {/* Suggested matches carousel */}
                    {rule.suggestedMatches && rule.suggestedMatches.length > 0 && (
                      <CopyMatchesCarousel
                        matches={rule.suggestedMatches}
                        disabled={copyingRuleId === rule.id}
                        onCopy={(matchRuleId, playbookName, samePlaybook) =>
                          copyInsteadOfExtracted(
                            matchRuleId,
                            rule.id,
                            playbookName,
                            section.name,
                            samePlaybook
                          )
                        }
                      />
                    )}

                    <Flex.H align="center" gap="2" wrap="wrap">
                      <Button
                        size="sm"
                        colorPalette="green"
                        variant={rule.accepted ? 'solid' : 'outline'}
                        onClick={() => setRuleField(section.id, rule.id, 'accepted', true)}
                      >
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        colorPalette="red"
                        variant={!rule.accepted ? 'solid' : 'outline'}
                        onClick={() => setRuleField(section.id, rule.id, 'accepted', false)}
                      >
                        Reject
                      </Button>
                      {playbookConfig?.paperType !== 'third_party' && (
                        <Flex.H align="center" gap="1">
                          <Switch
                            size="sm"
                            checked={rule.isFallback}
                            onChange={() =>
                              setRuleField(section.id, rule.id, 'isFallback', !rule.isFallback)
                            }
                          />
                          <Text fontSize="xs" color="fg.muted">
                            Fallback
                          </Text>
                          <Tooltip
                            trigger={
                              <LuInfo
                                size={12}
                                style={{ color: 'var(--chakra-colors-fg-muted)', cursor: 'help' }}
                              />
                            }
                            positioning={{ placement: 'top' }}
                          >
                            Mark as a fallback position — a compromise the company will accept if
                            the preferred language is rejected.
                          </Tooltip>
                        </Flex.H>
                      )}
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() =>
                          setSearchExpanded(searchExpanded === rule.id ? null : rule.id)
                        }
                      >
                        <LuSearch size={12} />
                        Find Similar
                      </Button>
                    </Flex.H>

                    {/* Manual search panel */}
                    {searchExpanded === rule.id && (
                      <ManualSearchPanel
                        initialQuery={rule.name}
                        excludePlaybookId={playbookConfig?.playbookId ?? ''}
                        disabled={copyingRuleId === rule.id}
                        onCopy={(matchRuleId, playbookName) =>
                          copyInsteadOfExtracted(matchRuleId, rule.id, playbookName, section.name)
                        }
                      />
                    )}
                  </>
                )}
              </Flex.V>
            </Box>
          ))}
        </Flex.V>
      ),
    };
  });

  return (
    <Flex.V gap="5" p="6">
      <Flex.H align="center" justify="space-between">
        <Text fontWeight="700" fontSize="lg">
          Review Extracted Rules
        </Text>
        <Button size="sm" variant="ghost" onClick={acceptAll}>
          Accept All
        </Button>
      </Flex.H>

      <Text fontSize="sm" color="fg.muted">
        {totalAccepted + totalMerged} of {totalRules} rules accepted
        {totalMerged > 0 && `, ${totalMerged} updating existing`}
        {totalCopied > 0 && `, ${totalCopied} copied`}
      </Text>

      {isSearchingMatches && (
        <Flex.H align="center" gap="2" px="2" py="1" bg="blue.subtle" borderRadius="md">
          <Spinner size="xs" />
          <Text fontSize="xs" color="blue.fg">
            Searching other playbooks for similar rules…
          </Text>
        </Flex.H>
      )}

      <Accordion items={accordionItems} multiple collapsible />

      <Flex.H gap="2" justify="flex-end">
        <Button variant="ghost" size="sm" onClick={onBack}>
          Back
        </Button>
        <Button
          colorPalette="gray"
          size="sm"
          onClick={handleSaveRules}
          disabled={isSaving || (totalAccepted === 0 && totalCopied === 0 && totalMerged === 0)}
          loading={isSaving}
        >
          Save {totalAccepted + totalCopied + totalMerged} Rules
        </Button>
      </Flex.H>
    </Flex.V>
  );
}
